import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function HeroAdmin() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/hero").then((r) => setForm(r.data)).catch(() => {}); }, []);

  if (!form) return <div className="font-body text-sm text-neutral-500" data-testid="hero-admin-loading">Loading…</div>;

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/hero", {
        overline: form.overline, line1: form.line1, accent1: form.accent1,
        line2: form.line2, accent2: form.accent2, paragraph: form.paragraph,
        scroll_hint: form.scroll_hint, available_text: form.available_text,
        available_for_work: !!form.available_for_work,
      });
      toast.success("Hero saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save.");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="hero-admin">
      <section className="lg:col-span-8 bg-white border border-neutral-200 p-6 space-y-1">
        <div>
          <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Overline (small line above headline)</label>
          <input value={form.overline || ""} onChange={onChange("overline")} className="input-bare" data-testid="hero-overline-input" />
        </div>
        <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          <div>
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Line 1 (big text)</label>
            <input value={form.line1 || ""} onChange={onChange("line1")} className="input-bare" data-testid="hero-line1-input" />
          </div>
          <div>
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Accent 1 (italic / orange)</label>
            <input value={form.accent1 || ""} onChange={onChange("accent1")} className="input-bare" data-testid="hero-accent1-input" />
          </div>
          <div className="pt-4">
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Line 2 (big text)</label>
            <input value={form.line2 || ""} onChange={onChange("line2")} className="input-bare" data-testid="hero-line2-input" />
          </div>
          <div className="pt-4">
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Accent 2 (italic)</label>
            <input value={form.accent2 || ""} onChange={onChange("accent2")} className="input-bare" data-testid="hero-accent2-input" />
          </div>
        </div>
        <div className="pt-6">
          <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Paragraph (intro under headline)</label>
          <textarea value={form.paragraph || ""} onChange={onChange("paragraph")} rows={3} className="input-bare resize-none" data-testid="hero-paragraph-input" />
        </div>
        <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          <div>
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Scroll hint</label>
            <input value={form.scroll_hint || ""} onChange={onChange("scroll_hint")} className="input-bare" data-testid="hero-scroll-input" />
          </div>
          <div>
            <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Header CTA text</label>
            <input value={form.available_text || ""} onChange={onChange("available_text")} className="input-bare" data-testid="hero-available-input" />
          </div>
        </div>

        <div className="pt-6">
          <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Availability</label>
          <button
            type="button"
            onClick={() => setForm((f) => ({ ...f, available_for_work: !f.available_for_work }))}
            className={`mt-3 inline-flex items-center gap-3 px-5 py-3 border font-body text-xs uppercase tracking-[0.2em] transition-colors ${
              form.available_for_work
                ? "border-[#FF3E00] bg-[#FF3E00] text-white"
                : "border-neutral-300 bg-white text-neutral-700"
            }`}
            data-testid="hero-availability-toggle"
            aria-pressed={!!form.available_for_work}
          >
            <span className={`w-2 h-2 rounded-full ${form.available_for_work ? "bg-white animate-pulse" : "bg-neutral-400"}`} />
            {form.available_for_work ? "Available for work — ON" : "Not available — OFF"}
          </button>
          <p className="font-body text-[11px] text-neutral-500 pt-2 leading-relaxed">
            When ON: the header shows a pulsing orange dot + your CTA text. When OFF: header shows a grey dot + &ldquo;Not available&rdquo;.
          </p>
        </div>
      </section>
      <aside className="lg:col-span-4 flex flex-col">
        <p className="font-body text-[11px] text-neutral-500 leading-relaxed mb-6">
          The headline animates as four lines in sequence: <strong>Line 1</strong> → <strong>Accent 1</strong> → <strong>Line 2</strong> → <strong>Accent 2</strong>. Accents render in italic; Accent 1 is highlighted in the brand orange. Leave any field empty to skip that line.
        </p>
        <button type="submit" disabled={saving} className="w-full bg-[#0A0A0A] text-[#F4F4F0] py-4 font-display font-bold uppercase tracking-widest text-sm hover:bg-[#FF3E00] transition-colors disabled:opacity-40" data-testid="hero-save-button">
          {saving ? "Saving…" : "Save Hero"}
        </button>
      </aside>
    </form>
  );
}
